import React, { useState, useMemo, useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';
import { Formik, Form, FormikErrors, FormikProps } from 'formik';
import { CopyIcon } from '@rsos/assets/icons';
import { AttentionTriangleIcon, InfoIcon } from '@rsos/base-assets/icons';
import { Box, Button, Text } from '@rsos/base-components';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { type EventShare } from '@rsos/reduxDataStore/src/features/eventShare';
import {
  useCreateEventShareMutation,
  useGetEventSharesByPhoneQuery,
  useGetIarAgenciesQuery,
} from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import type { CreateEventShareRequest } from '@rsos/reduxDataStore/src/features/eventShare/types';
import { extractErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import {
  notifyDefault,
  notifyError,
  THIRTY_SECONDS,
} from '@rsos/utils/toastUtils';
import { FieldNotes } from '../';
import { getConnectShareUrl } from '../../../../utils/getConnectShareUrl';
import type { RootState as TranscriptionRootState } from '../../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice/transcriptionRootState';
import { selectTranscriptionForCall } from '../../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice/transcriptionSelectors';
import {
  FIELD_DIRECT_RECIPIENTS_SHARE,
  FIELD_IAR_SHARE,
  FIELD_SHARE_LINK_SHARE,
  type FieldSharingMethod,
  isLinkBasedShare,
} from '../../constants/shareMethods';
import DataSourcesAndInvites from './DataSourcesAndInvites';
import { LimitReachedWrapper, ShareLinkContainer } from './FieldShare.styles';
import FieldShareStepButtons from './FieldShareStepButtons';
import IarAgencySelector from './IarAgencySelector';
import ShareEventRecipients, {
  type MultiInputTag,
} from './ShareEventRecipients';
import ToastMessage from './ToastMessage';
import {
  SHARE_STEP,
  type FieldFormValues,
  type StepId,
  createValidationSchema,
  createInitialValues,
  isStepValid,
  createSubmissionPayload,
  getNextStep,
  getPrevStep,
} from './helpers';

const FieldShare = ({ onClose }: { onClose: () => void }) => {
  const [currentStep, setCurrentStep] = useState<StepId>(SHARE_STEP.RECIPIENTS);
  const [sharingMethod, setSharingMethod] = useState<FieldSharingMethod>(
    FIELD_DIRECT_RECIPIENTS_SHARE,
  );
  const [shareResult, setShareResult] = useState<{
    shareLink: string;
    success: boolean;
    shareData?: EventShare;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allowSubmission, setAllowSubmission] = useState(false);

  const selectedCallerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );
  const selectedCall = useSelectedCall();
  // Read the live transcription lazily at submit (see handleSubmit) instead of
  // subscribing the modal to the high-frequency transcription slice, which
  // would re-render the whole form on every streamed token.
  const store = useStore();

  const [createEventShare] = useCreateEventShareMutation();

  const orgName = useCurrentOrgName();

  // Format the URI from the selected caller ID
  const uri = selectedCallerID ? `tel://${selectedCallerID}` : '';

  // Fetch IaR agencies when the IaR method is selected — skipped otherwise
  const { data: iarAgencies = [], isLoading: iarAgenciesLoading } =
    useGetIarAgenciesQuery(
      { org: orgName ?? '' },
      { skip: !orgName || sharingMethod !== FIELD_IAR_SHARE },
    );

  // Fetch existing shares for the current phone number
  const { data: existingShares = [] } = useGetEventSharesByPhoneQuery(
    {
      org: orgName || '',
      phoneNumber: uri,
    },
    {
      skip: !orgName || !uri,
    },
  );

  // Count active shares
  const activeSharesCount = useMemo(() => {
    return existingShares.filter(share => share.status === 'active').length;
  }, [existingShares]);

  // Check if sharing limit is reached (3 active shares)
  const isShareLimitReached = activeSharesCount >= 3;

  const validationSchema = useMemo(
    () => createValidationSchema(currentStep, sharingMethod),
    [currentStep, sharingMethod],
  );

  // Memoized initial values to prevent Formik re-initialization
  const initialValues = useMemo(
    () => createInitialValues(sharingMethod),
    [sharingMethod],
  );

  const handleSubmit = async (values: FieldFormValues) => {
    if (!orgName || !uri) {
      notifyError('Organization or caller ID not available');
      return;
    }

    setIsSubmitting(true);
    try {
      const transcription = selectTranscriptionForCall(selectedCallerID)(
        store.getState() as TranscriptionRootState,
      );
      const submissionData = createSubmissionPayload(
        values,
        sharingMethod,
        uri,
        orgName,
        selectedCall,
        transcription,
      );

      const result = await createEventShare({
        org: orgName,
        ...(submissionData as CreateEventShareRequest),
      }).unwrap();

      notifyDefault({
        message: <ToastMessage selectedCallerID={selectedCallerID} />,
        customIcon: InfoIcon,
        options: { autoClose: THIRTY_SECONDS, closeButton: true },
      });

      // Only the share-link method shows the copyable link step. IaR creates a
      // session but shares to the agency roster (no link to copy), so — like
      // Direct Recipients — it closes the modal on success.
      if (sharingMethod === FIELD_SHARE_LINK_SHARE) {
        const shareKey = result?.recipients?.sender?.share_key || '';
        const shareLink = shareKey ? getConnectShareUrl(shareKey) : '';
        setShareResult({
          shareLink,
          success: true,
          shareData: result,
        });
        setCurrentStep(SHARE_STEP.SHARE_LINK);
      } else {
        // Direct Recipients and IaR close the modal on success
        onClose();
      }
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error, 'Failed to create share');
      notifyError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    const next = getNextStep(currentStep, sharingMethod);
    if (next !== null) setCurrentStep(next);
  };

  const handleBack = () => {
    const prev = getPrevStep(currentStep, sharingMethod);
    if (prev !== null) setCurrentStep(prev);
  };

  const validateStep = (
    step: StepId,
    values: FieldFormValues,
    errors: FormikErrors<FieldFormValues>,
  ) => isStepValid(step, values, errors, sharingMethod);

  const createSharingMethodHandler = useCallback(
    (
      setFieldValue: FormikProps<FieldFormValues>['setFieldValue'],
      validateForm: FormikProps<FieldFormValues>['validateForm'],
    ) =>
      (method: FieldSharingMethod) => {
        setSharingMethod(method);
        setFieldValue('sharingMethod', method);
        setCurrentStep(SHARE_STEP.RECIPIENTS);

        // Clear recipient input when switching away from direct recipients
        if (isLinkBasedShare(method)) {
          setFieldValue('recipients', []);
        }

        // Clear IaR selection when switching away from IaR
        if (method !== FIELD_IAR_SHARE) {
          setFieldValue('iarSelectedMembers', []);
        }

        // Trigger validation after React re-render cycle completes
        setTimeout(() => validateForm(), 0);
      },
    [setSharingMethod],
  );

  const handleCopyLink = () => {
    if (shareResult?.shareLink) {
      navigator.clipboard.writeText(shareResult.shareLink);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        // Only allow submission if explicitly triggered by user
        if (allowSubmission) {
          setAllowSubmission(false);
          handleSubmit(values);
        }
      }}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, validateForm }) => {
        const handleSharingMethodChange = createSharingMethodHandler(
          setFieldValue,
          validateForm,
        );

        return (
          <Form>
            {isShareLimitReached && currentStep !== SHARE_STEP.SHARE_LINK && (
              <LimitReachedWrapper>
                <AttentionTriangleIcon height={16} width={16} />
                <Text size="normal">
                  Limit reached: Max 3 active shares per event.
                </Text>
              </LimitReachedWrapper>
            )}
            <Box mb="24px">
              {currentStep === SHARE_STEP.RECIPIENTS && (
                <>
                  <ShareEventRecipients
                    onRecipientsChange={(recipients: MultiInputTag[]) =>
                      setFieldValue('recipients', recipients)
                    }
                    onSharingMethodChange={handleSharingMethodChange}
                    initialRecipients={values.recipients}
                    selectedSharingMethod={sharingMethod}
                    error={
                      typeof errors.recipients === 'string'
                        ? errors.recipients
                        : undefined
                    }
                    isDisabled={isShareLimitReached}
                  />
                </>
              )}

              {currentStep === SHARE_STEP.IAR_SELECT && (
                <Box>
                  {iarAgenciesLoading ? (
                    <Text color="secondaryText" size="normal">
                      Loading agencies...
                    </Text>
                  ) : (
                    <IarAgencySelector
                      agencies={iarAgencies}
                      selectedMembers={values.iarSelectedMembers}
                      org={orgName ?? ''}
                      onChange={members =>
                        setFieldValue('iarSelectedMembers', members)
                      }
                    />
                  )}
                </Box>
              )}

              {currentStep === SHARE_STEP.DATA_SOURCES && (
                <>
                  <DataSourcesAndInvites
                    sources={values.dataSources}
                    onChange={setFieldValue}
                  />
                  {errors.dataSources && (
                    <Text color="lightDanger" size="small" mt="8px">
                      {typeof errors.dataSources === 'string'
                        ? errors.dataSources
                        : 'Please select at least one data source'}
                    </Text>
                  )}
                </>
              )}

              {currentStep === SHARE_STEP.NOTES && (
                <FieldNotes
                  value={values.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFieldValue('notes', e.target.value)
                  }
                />
              )}

              {currentStep === SHARE_STEP.SHARE_LINK && shareResult && (
                <Box>
                  <Text size="normal" color="secondaryText">
                    {shareResult.success ? 'Share Link:' : 'Share Failed'}
                  </Text>

                  {shareResult.success && (
                    <>
                      <ShareLinkContainer>
                        <Text size="normal" color="secondaryText" mb="12px">
                          {shareResult.shareLink}
                        </Text>
                      </ShareLinkContainer>
                      <Button
                        btnType="primary"
                        type="button"
                        onClick={handleCopyLink}
                        size="small"
                      >
                        <Box horizontal flow={8} align="center">
                          <CopyIcon />
                          <Text size="normal">Copy Link</Text>
                        </Box>
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>

            <Box horizontal flow={8}>
              <FieldShareStepButtons
                currentStep={currentStep}
                isStepValid={validateStep(currentStep, values, errors)}
                isSubmitting={isSubmitting}
                isShareLimitReached={isShareLimitReached}
                onCancel={onClose}
                onClose={onClose}
                onBack={handleBack}
                onNext={handleNext}
                onSubmit={() => setAllowSubmission(true)}
              />
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default FieldShare;
